import { getLocalTimeZone } from '@internationalized/date';

import { formatDaysUntil, toCurrency } from '../../utils/formatting';
import Widget from '../Widget';

import type { CalendarDate } from '@internationalized/date';

interface ICreditCardStatementProps {
  /** Balance owed at the close of the cycle, in major currency units. */
  statementBalance?: string | null;
  /** Smallest payment that keeps the card current this cycle, in major currency units. */
  minAmountDue?: string | null;
  dueDate?: CalendarDate | null;
  /** ISO code the bank reported the figures in. Defaults to USD when absent. */
  currencyCode?: string | null;
}

/**
 * What the card's last statement asks for, as distinct from what the card is at
 * right now. Banks report these unevenly, so each row renders only when its
 * figure is present rather than showing a placeholder for a number the bank
 * never sent.
 */
const CreditCardStatement = ({ statementBalance, minAmountDue, dueDate, currencyCode }: ICreditCardStatementProps) => {
  const rows: { label: string; value: string; hint?: string }[] = [];

  if (statementBalance !== null && statementBalance !== undefined) {
    rows.push({ label: 'Statement balance', value: toCurrency(parseFloat(statementBalance), { currencyCode }) });
  }
  if (minAmountDue !== null && minAmountDue !== undefined) {
    rows.push({ label: 'Minimum payment', value: toCurrency(parseFloat(minAmountDue), { currencyCode }) });
  }
  if (dueDate) {
    rows.push({
      label: 'Payment due',
      // Local midnight, formatted locally — `toDate('UTC')` would render the
      // day before for any viewer west of Greenwich.
      value: dueDate
        .toDate(getLocalTimeZone())
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      hint: formatDaysUntil(dueDate),
    });
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <Widget>
      <Widget.Header title="Statement" />
      <Widget.Body>
        <dl className="flex flex-col gap-3 p-5">
          {rows.map((row) => (
            <div key={row.label} className="flex flex-row items-baseline justify-between gap-4">
              <dt className="text-tertiary text-sm">{row.label}</dt>
              <dd className="flex flex-row items-baseline gap-2">
                <span className="text-primary text-md font-semibold">{row.value}</span>
                {row.hint && <span className="text-tertiary text-xs">{row.hint}</span>}
              </dd>
            </div>
          ))}
        </dl>
      </Widget.Body>
    </Widget>
  );
};

export default CreditCardStatement;
